/* oxlint-disable typescript/no-explicit-any */
import { type RefObject, useCallback, useRef } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

/**
 * Wraps callback functions with error handling to show error boundary on exceptions
 *
 * Note: The user defined callbacks (such as onChange) are called inside the transaction filter of the configuration effect.
 * To always call the most recent function, we need to store it in a ref and use the current value when invoking the function.
 * @internal
 */
export const useUpdatableCallback = <T extends (...args: any[]) => void>(
  fn?: T,
): RefObject<T | undefined> => {
  const callbackRef = useRef<T | undefined>(fn);
  const { showBoundary } = useErrorBoundary();

  const errorAwareCallback = useCallback<T>(
    ((...args: any[]) => {
      try {
        fn?.(...args);
      } catch (err) {
        showBoundary(err);
      }
    }) as T,
    [showBoundary, fn],
  );

  if (callbackRef.current !== errorAwareCallback) {
    callbackRef.current = errorAwareCallback;
  }

  return callbackRef;
};
